import { node } from "~/util/dom";
import { ICommonOpts } from "~/report/interfaces";

interface IOpts extends ICommonOpts {
	textMinimal?: string;
	textMaximal?: string;
	textLong?: string;
	textShort?: string;
	isExpanded?: boolean;
	isMinimized?: boolean;
	isMaximized?: boolean;
	setMultiple?: boolean;
	accept?: string;
	onChange: (files: Array<File>) => void;
}

export default class ReportFormFileloader {
	#opts: IOpts;

	#container: HTMLElement;

	#dropZone: HTMLElement;

	#inputNode: HTMLInputElement;

	#textNode: HTMLElement;

	#state: "expanded" | "minimized" | "maximized" | "condensed";

	constructor(opts: IOpts) {
		this.#opts = {
			onChange: () => { /**/ },
			textMinimal: "",
			textMaximal: "",
			textLong: "",
			textShort: "",
			parent: null,
			icon: null,
			isExpanded: false,
			isMinimized: false,
			isMaximized: false,
			setMultiple: false,
			accept: "*/*",
			...opts,
		};
		this.#build();
	}

	handleEvent(event) {
		switch (event.type) {
			case "drop":
				event.preventDefault();
				event.stopPropagation();
				this.#dropZone.classList.remove("focus");
				if (event.dataTransfer && event.dataTransfer.files) {
					this.#opts.onChange(Array.from(event.dataTransfer.files));
					this.#inputNode.value = "";
				}
				break;
			case "dragover":
				event.preventDefault();
				break;
			case "dragenter":
			case "focus":
				this.#dropZone.classList.add("focus");
				break;
			case "dragleave":
			case "blur":
				this.#dropZone.classList.remove("focus");
				break;
			case "change":
				this.#opts.onChange(Array.from(this.#inputNode.files));
				this.#inputNode.value = "";
				break;
			default:
		}
	}

	get isExpanded() {
		return this.#state === "expanded";
	}

	get isMinimized() {
		return this.#state === "minimized";
	}

	get isMaximized() {
		return this.#state === "maximized";
	}

	get isCondensed() {
		return this.#state === "condensed";
	}

	get inputNode() {
		return this.#inputNode;
	}

	minimize() {
		this.#state = "minimized";
		this.#container.classList.remove("maximized", "expanded");
		this.#container.classList.add("minimized");
		this.#textNode.innerText = this.#opts.textMinimal;
	}

	maximize() {
		this.#state = "maximized";
		this.#container.classList.remove("minimized", "expanded");
		this.#container.classList.add("maximized");
		this.#textNode.innerText = this.#opts.textMaximal;
	}

	expand() {
		this.#state = "expanded";
		this.#container.classList.remove("maximized", "minimized");
		this.#container.classList.add("expanded");
		this.#textNode.innerText = this.#opts.textLong;
	}

	condense() {
		this.#state = "condensed";
		this.#container.classList.remove("expanded", "minimized", "maximized");
		this.#textNode.innerText = this.#opts.textShort;
	}

	hide() {
		this.#container.classList.add("hidden");
	}

	show() {
		this.#container.classList.remove("hidden");
	}

	#build() {
		const { parent, icon, isExpanded, isMinimized, isMaximized } = this.#opts;

		this.#container = node("div", { className: "file-loader-container" });

		this.#dropZone = node("label", { className: "file-loader" });

		this.#dropZone.addEventListener("drop", this);
		this.#dropZone.addEventListener("dragover", this);
		this.#dropZone.addEventListener("dragenter", this);
		this.#dropZone.addEventListener("dragleave", this);

		this.#inputNode = node("input", { className: "file-loader-input", type: "file", multiple: this.#opts.setMultiple, accept: this.#opts.accept });

		this.#inputNode.addEventListener("focus", this);
		this.#inputNode.addEventListener("blur", this);
		this.#inputNode.addEventListener("change", this);

		this.#textNode = node("span", { className: "file-loader-text" });

		if (isExpanded) {
			this.expand();
		} else if (isMinimized) {
			this.minimize();
		} else if (isMaximized) {
			this.maximize();
		} else {
			this.condense();
		}

		this.#dropZone.append(icon, this.#inputNode, this.#textNode);

		this.#container.append(this.#dropZone);

		parent.append(this.#container);
	}
}
